import React, { useCallback, useEffect, useRef, useState } from 'react'
import '../../styles/CreateOwnContent.css'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import { ReactComponent as ContentTemplateIcon } from '../../assets/svg/ContentTemplateIcon.svg'
import { ReactComponent as OptionRemove } from '../../assets/svg/DeleteContent.svg'
import { ReactComponent as ImageAddIcon } from '../../assets/svg/ImageAddIcon.svg'
import Switcher from './Switcher'
import ReactTooltip from 'react-tooltip'
import TargetGroupModal from '../modals/TargetGroupModal'
import ContentTemplate from './ContentTemplate'
import { createContent, upload, sendNotification, saveTemplate as saveTemplateRequest, updateTemplateById } from '../../server/server'
import { fileSystemUrl, videoUrl } from '../../constants/Connections'
import { useUser } from '../../auth'
import { format_day, isValidURL, notify, translateTargetText } from '../../utils/util'
import { useHistory } from 'react-router-dom'
import { useSocket } from '../../context/socket'
import { MINIMAL_NUMBER_OF_POLL_OPTIONS, OPTION_TYPE_DEFAULT, REPEAT_CONTENT_INIT, SCHEDULE_CONTENT_INIT, TARGET_GROUP_INIT } from '../../constants/Constants'
import Loading from '../Loading'
import ScheduleExpandedModal from '../modals/ScheduleExpandendModal'
import ScheduleSwitcher from './ScheduleSwitcher'
import i18n from '../../i18n'
import useRebuildTooltips from '../../hooks/useRebuildTooltips'
import SaveTemplateModal from './templates/SaveTemplateModal'
import { VISIBILITY_PRIVATE, authorLabel, toTemplateCopy, useTemplateScope } from './templates/templateUtils'

const MAX_FILE_SIZE = 15 * 1024 * 1024
const EMPTY_OPTIONS = ['', '', '']
const POLL_OPTION_TOOLTIP_ID = 'poll-option-remove'
// Poll option rows past this index share the same entrance delay.
const OPTION_STAGGER_CAP = 8

const TEMPLATE_LIST_CLOSED = { opened: false, focusId: null }

function CreateOwnContent({ content, type = 'feed', editMode = false }) {
    const { t } = useTranslation()
    const [templateList, setTemplateList] = useState(TEMPLATE_LIST_CLOSED)
    const [editTemplateMode, setEditTemplateMode] = useState(false)
    const [isTemplate, setIsTemplate] = useState(false)
    const [selectedContent, setSelectedContent] = useState({})
    const [templateScope, setTemplateScope] = useTemplateScope()

    useEffect(() => {
        setSelectedContent(content)
    }, [content])

    // Both template modes only make sense for the form that was opened from the
    // list. Reset them on every open/close so a form remounted after the list
    // closes is a plain create/edit form again (otherwise it came back in
    // "update template" mode with no Publish button and an undefined id).
    const resetTemplateModes = () => {
        setEditTemplateMode(false)
        setIsTemplate(false)
    }

    const clickOnUseTemplateButton = () => {
        resetTemplateModes()
        if (templateList.opened) {
            setSelectedContent(editMode ? content : {})
            setTemplateList(TEMPLATE_LIST_CLOSED)
        } else {
            setTemplateList({ opened: true, focusId: null })
        }
    }

    const selectTemplateToEdit = (template) => {
        setSelectedContent(template)
        setEditTemplateMode(true)
        setIsTemplate(false)
        setTemplateList(TEMPLATE_LIST_CLOSED)
    }

    // "Use template" fills the form with a detached copy: publishing or saving
    // from it creates new documents and never touches the saved original.
    const selectContent = (template) => {
        setIsTemplate(true)
        setEditTemplateMode(false)
        setSelectedContent(toTemplateCopy(template))
        setTemplateList(TEMPLATE_LIST_CLOSED)
    }

    // Called after an update lands (post-await, so React 17 won't batch): keep
    // the open flag and the id to scroll to in one state object -> one commit.
    const openTemplateList = useCallback((focusId = null) => {
        setEditTemplateMode(false)
        setIsTemplate(false)
        setTemplateList({ opened: true, focusId })
    }, [])

    return (
        <div>
            {type !== 'survey' && (
                <button
                    type="button"
                    id="use-template-button"
                    className="coc-template-button w-full rounded-full py-2 flex justify-center items-center"
                    aria-pressed={templateList.opened}
                    onClick={clickOnUseTemplateButton}
                >
                    <span className="mr-2 text-base font-semibold">{t('useTemplate')}</span>
                    <ContentTemplateIcon fill="currentColor" aria-hidden="true" />
                </button>
            )}
            <div key={templateList.opened ? 'templates' : 'form'} className="coc-panel-enter">
                {templateList.opened ? (
                    <ContentTemplate
                        setSelTemplate={selectContent}
                        type={type}
                        selectTemplateToEdit={selectTemplateToEdit}
                        focusTemplateId={templateList.focusId}
                        scope={templateScope}
                        onScopeChange={setTemplateScope}
                    />
                ) : (
                    <ContentForm
                        selectedContent={selectedContent}
                        editMode={editMode}
                        editTemplateMode={editTemplateMode}
                        isTemplate={isTemplate}
                        type={type}
                        openTemplateList={openTemplateList}
                    />
                )}
            </div>
        </div>
    )
}

const ContentForm = ({ selectedContent = {}, editMode = false, isTemplate = false, editTemplateMode = false, type = 'feed', openTemplateList = () => {} }) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const history = useHistory()
    const { socket } = useSocket()
    const [progress, setProgress] = useState(false)
    const [savingTemplate, setSavingTemplate] = useState(false)
    const [titleError, setTitleError] = useState(false)
    const [targetError, setTargetError] = useState(false)
    const [isVideo, setIsVideo] = useState(false)
    const [templateSaved, setTemplateSaved] = useState(false)
    const [saveModalOpen, setSaveModalOpen] = useState(false)
    const [showErrorMessage, setShowErrorMessage] = useState(false)
    const [optionsError, setOptionsError] = useState(false)
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [link, setLink] = useState('')
    const [image, setImage] = useState(undefined)
    const [mediaLoaded, setMediaLoaded] = useState(false)
    const [selectedFile, setSelectedFile] = useState(undefined)
    const [openTarget, setOpenTarget] = useState(false)
    const [requiredEnabled, setRequiredEnabled] = useState(false)
    const [anonymousEnabled, setAnonymousEnabled] = useState(false)
    const [trackWellbeingEnabled, setTrackWellbeingEnabled] = useState(false)
    const [addedToAnalytics, setAddedToAnalytics] = useState(false)
    const [returnTarget, setReturnTarget] = useState(TARGET_GROUP_INIT)
    const [openSchedule, setOpenSchedule] = useState(false)
    const [returnSchedule, setReturnSchedule] = useState(SCHEDULE_CONTENT_INIT)
    const [returnRepeat, setReturnRepeat] = useState(REPEAT_CONTENT_INIT)
    const [options, setOptions] = useState(EMPTY_OPTIONS)
    const isFirstRender = useRef(true)
    // Success paths navigate away or swap to the template list, which unmounts
    // this form; every post-await setState is guarded with this.
    const mountedRef = useRef(true)
    const fileInputRef = useRef(null)
    // Rows present at mount stagger in together; rows added later enter alone.
    const initialOptionCountRef = useRef(options.length)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (!!selectedContent?.type && (editMode || isTemplate || editTemplateMode)) {
            setTitle(selectedContent.title ?? '')
            setMessage(selectedContent.message ?? '')
            setOptions(Array.isArray(selectedContent.options) && selectedContent.options.length > 0 ? selectedContent.options : EMPTY_OPTIONS)
            setLink(selectedContent.url ?? '')
            setRequiredEnabled(!!selectedContent?.required)
            setAnonymousEnabled(!!selectedContent?.anonymous)
            setTrackWellbeingEnabled(!!selectedContent?.trackWellbeing)
            setReturnTarget({
                selection: selectedContent?.targetOptions?.selection ?? [],
                type: selectedContent?.targetOptions?.type ?? 'root',
                target: selectedContent?.target ?? [],
                name: selectedContent?.target?.length > 0 ? selectedContent?.target_text : 'Select target',
            })
            setReturnSchedule({
                enabled: selectedContent?.schedule,
                text: 'On',
                recurrentText: selectedContent?.repeatData?.recurrentText ?? 'Never',
                repeatPeriodText: selectedContent?.repeatData?.repeatPeriodText ?? 'Never',
                repeatEnabled: !!selectedContent?.repeatData?.repeatEnabled,
                schedule_date: selectedContent?.schedule_date ?? 0,
                repeatType: selectedContent?.repeatData?.repeatType ?? 'weekly',
                selectedDays: selectedContent?.repeatData?.selectedDays ?? [],
                selectedDay: selectedContent?.repeatData?.selectedDay ?? 1,
                endOfRecurrenceDate: selectedContent?.repeatData?.endOfRecurrenceDate ?? 0,
            })
            setReturnRepeat(selectedContent?.repeatData ?? REPEAT_CONTENT_INIT)
            if (!!selectedContent?.files) {
                const isVideoFile = selectedContent.files?.type === 'video'
                const mediaUrl = isVideoFile ? videoUrl : fileSystemUrl
                setIsVideo(isVideoFile)
                setImage(`${mediaUrl}/${selectedContent.files?.storage_name}?token=${user.token}&width=334`)
            }
            initialOptionCountRef.current = selectedContent.options?.length ?? EMPTY_OPTIONS.length
        }
    }, [selectedContent])

    useEffect(() => {
        if (returnTarget?.target?.length < 3) {
            setAnonymousEnabled(false)
        }
    }, [returnTarget?.target])

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        if (!selectedFile) {
            setImage('')
            return
        }

        const objectUrl = URL.createObjectURL(selectedFile)
        setImage(objectUrl)

        return () => URL.revokeObjectURL(objectUrl)
    }, [selectedFile])

    useEffect(() => {
        setTargetError(false)
    }, [returnTarget])

    // A saved template is a snapshot; once the form diverges it can be saved again.
    useEffect(() => {
        setTemplateSaved(false)
    }, [title, message, link, options, image, returnTarget, returnSchedule, requiredEnabled, anonymousEnabled, trackWellbeingEnabled])

    // The media element remounts on every url change (keyed), so reset the fade.
    useEffect(() => {
        setMediaLoaded(false)
    }, [image])

    // The remove buttons only exist past the minimum count; bind them as they appear.
    useRebuildTooltips(options.length)

    const pressUploadPicture = () => {
        fileInputRef.current?.click()
    }
    const closeImage = () => {
        setImage(undefined)
        setSelectedFile(undefined)
    }
    const onImageChange = (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedFile(undefined)
            return
        }
        const file = e.target.files[0]
        if (file.size > MAX_FILE_SIZE) {
            notify(t('fileIsToBig'), 'error')
            setSelectedFile(undefined)
            return
        }
        setIsVideo(file.type.includes('video'))
        setSelectedFile(file)
    }
    const resetInputValue = (e) => {
        e.target.value = null
    }
    const setSchedule = () => {
        setReturnSchedule(returnSchedule.enabled ? returnSchedule : SCHEDULE_CONTENT_INIT)
        setOpenSchedule(true)
    }
    const closeTargetModal = () => {
        setOpenTarget(false)
    }
    const closeScheduleModal = () => {
        setOpenSchedule(false)
    }
    const handleOptionInput = (e, index) => {
        const value = e.target.value
        setOptions((prev) => prev.map((option, i) => (i === index ? value : option)))
    }
    const handleOptionRemove = (index) => {
        setOptions((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
    }
    const handleOptionAdd = () => {
        setOptions((prev) => [...prev, ''])
    }

    const switchAnonymous = (state) => {
        const numberOfTarget = returnTarget?.target?.length ?? 0
        // Same threshold as the effect that clears the flag when the target shrinks.
        if (state && numberOfTarget > 0 && numberOfTarget < 3) {
            notify(t(numberOfTarget === 1 ? 'Cannot create anonymous poll for 1 user' : 'Cannot create anonymous poll for 2 users'), 'error')
            return
        }
        setTrackWellbeingEnabled(false)
        setAnonymousEnabled(state)
    }

    const switchAddWellbeingTracker = (state) => {
        setTrackWellbeingEnabled(state)
        setAnonymousEnabled(false)
    }

    const prepareData = () => {
        let content = {
            ...(selectedContent || {}),
            title,
            message,
            type,
            status: 'start',
            target: returnTarget?.target?.map((e) => e.id ?? e) ?? [],
            target_text: returnTarget?.name,
            institution_id: user.institution_id,
            unfilteredTarget: returnTarget?.target,
            targetOptions: {
                selection: returnTarget?.selection,
                type: returnTarget?.type,
            },
            schedule: returnSchedule?.enabled,
            schedule_date: returnSchedule?.schedule_date === 0 ? null : returnSchedule?.schedule_date,
            required: requiredEnabled,
            anonymous: anonymousEnabled,
            user_id: editMode && selectedContent?.id ? selectedContent?.user_id : user.id,
            target_count: returnTarget.target?.length ?? 0,
            created_date: !editMode ? Date.now() : selectedContent?.created_date,
            trackWellbeing: trackWellbeingEnabled,
            repeatData: {
                repeat: returnRepeat?.repeat,
                recurrentType: returnRepeat?.recurrentType === '' ? null : returnRepeat?.recurrentType,
                selectedDay: returnRepeat?.selectedDay,
                selectedDays: returnRepeat?.selectedDays?.length === 0 ? null : returnRepeat?.selectedDays,
                endOfRecurrenceDate: returnRepeat?.endOfRecurrenceDate === 0 ? null : returnRepeat?.endOfRecurrenceDate,
                repeatPeriodText: returnRepeat?.repeatPeriodText ?? 'Never',
            },
            repeat: returnRepeat?.repeat,
            repeatPeriodText: returnRepeat?.repeatPeriodText ?? 'Never',
            url: isValidURL(link) ? link : '',
        }
        if (type === 'poll') {
            content = {
                ...content,
                options: options?.filter((e) => !!e),
                option_type: OPTION_TYPE_DEFAULT,
                addedToAnalytics: addedToAnalytics,
            }
        } else if (type === 'feed') content = { ...content, likes: selectedContent?.likes || 0, views: selectedContent?.views || 0 }
        content.publicationDate = content.schedule ? content.schedule_date : content.created_date
        if (isTemplate) delete content['id']
        return content
    }
    const validate = () => {
        let valid = true
        if (title.trim() === '') {
            setTitleError(true)
            valid = false
        }
        if (returnTarget?.target?.length === 0) {
            setTargetError(true)
            valid = false
        }
        if (type === 'poll' && options?.filter((e) => !!e).length < MINIMAL_NUMBER_OF_POLL_OPTIONS) {
            if (options.length < MINIMAL_NUMBER_OF_POLL_OPTIONS) {
                setOptions([...options, ...Array(MINIMAL_NUMBER_OF_POLL_OPTIONS - options.length).fill('')])
            }
            setOptionsError(true)
            valid = false
        }
        setShowErrorMessage(!valid)
        return valid
    }

    /**
     * Resolves the `files` payload for a save: the freshly uploaded file, the
     * existing one, or nothing when the user removed the media. `upload()` never
     * rejects (it returns `{status, data}` even on failure), so success has to be
     * checked on the envelope, not on truthiness.
     */
    const resolveMedia = async (existingFiles) => {
        if (!image) return { ok: true, files: undefined }
        if (!selectedFile) return { ok: true, files: existingFiles }
        const req = await upload(selectedFile)
        if (req?.status === 200 && req.data && typeof req.data === 'object') return { ok: true, files: req.data }
        notify(t('imageIsNotUploaded'), 'error')
        return { ok: false }
    }

    // Each resolves to true on success so the visibility modal knows to close.
    const saveTemplate = async (data, visibility) => {
        const resp = await saveTemplateRequest({ type, visibility, data })
        if (resp?.status === 200) {
            if (mountedRef.current) setTemplateSaved(true)
            notify(t(resp?.data?.visibility === 'school' ? 'templateSharedAndSaved' : 'templateIsCreated'))
            return true
        }
        notify(t('templateIsNotCreated'), 'error')
        return false
    }
    const updateTemplate = async (data, visibility) => {
        const resp = await updateTemplateById({ type, id: selectedContent.id, visibility, data })
        if (resp?.status === 200) {
            notify(t('templateIsUpdated'))
            // Clear the busy flag before the list replaces this form.
            if (mountedRef.current) setSavingTemplate(false)
            openTemplateList(selectedContent.id)
            return true
        }
        notify(t('templateIsNotUpdated'), 'error')
        return false
    }

    // Validate first so the visibility step never opens for a form that cannot be saved.
    const openSaveTemplate = () => {
        if (savingTemplate || !validate()) return
        setSaveModalOpen(true)
    }

    const handleSaveTemplate = async (visibility) => {
        if (savingTemplate) return false
        setSavingTemplate(true)
        let saved = false
        try {
            const content = prepareData()
            const media = await resolveMedia(content?.files)
            if (!media.ok) return false
            const data = { ...content, files: media.files }
            saved = editTemplateMode ? await updateTemplate(data, visibility) : await saveTemplate(data, visibility)
            return saved
        } catch (error) {
            console.error('Template save failed', error)
            notify(t(editTemplateMode ? 'templateIsNotUpdated' : 'templateIsNotCreated'), 'error')
            return false
        } finally {
            if (mountedRef.current) {
                setSavingTemplate(false)
                if (saved) setSaveModalOpen(false)
            }
        }
    }

    const handlePublish = async () => {
        if (progress || !validate()) return
        setProgress(true)
        try {
            const content = prepareData()
            const media = await resolveMedia(content?.files)
            if (!media.ok) return

            if (editMode && content.id) {
                const { files, ...contentWithoutFiles } = content
                const data = {
                    ...(image ? { ...content, files: media.files } : contentWithoutFiles),
                    id: selectedContent.id,
                }
                const resp = await createContent({ data })
                if (resp?.status === 200) {
                    const saved = resp.data
                    history.push('/dashboard')
                    if (saved?.status !== 'pending') {
                        notify(t('yourDataIsUpdated'))
                    } else {
                        socket?.emit('new_content_reviewing', saved)
                        sendNotification({ content: saved, type: 'toReview' })
                        notify(t('contentNeedsToBeReviewedByTheScoolAdmin'), 'info')
                    }
                } else {
                    notify(t('yourDataIsNotUpdated'), 'error')
                }
            } else {
                const resp = await createContent({ data: { ...content, files: media.files } })
                if (resp?.status === 200) {
                    const saved = resp.data
                    history.push('/dashboard')
                    if (saved?.status !== 'pending') {
                        socket?.emit('new_content', saved)
                        sendNotification({ content: saved, type: 'task' })
                        notify(t('contentIsCreated'))
                    } else {
                        socket?.emit('new_content_reviewing', saved)
                        sendNotification({ content: saved, type: 'toReview' })
                        notify(t('contentNeedsToBeReviewedByTheScoolAdmin'), 'info')
                    }
                } else {
                    notify(t('contentIsNotCreated'), 'error')
                }
            }
        } catch (error) {
            console.error('Publish failed', error)
            notify(t(editMode && selectedContent?.id ? 'yourDataIsNotUpdated' : 'contentIsNotCreated'), 'error')
        } finally {
            // Never leave the button spinning after a failure.
            if (mountedRef.current) setProgress(false)
        }
    }

    const setReturnDataPreStep = (data) => {
        setReturnSchedule(data)
        setReturnRepeat({
            repeatText: data?.recurrentText ?? 'Never',
            repeat: !!data?.repeatEnabled,
            type: data?.repeatType ?? 'weekly',
            selectedDays: data?.selectedDays ?? [],
            selectedDay: data?.selectedDay ?? 1,
            endOfRecurrenceDate: data?.endOfRecurrenceDate ?? 0,
        })
    }

    const hasTarget = returnTarget?.target?.length > 0
    const isUpdate = editMode && !!selectedContent?.id

    return (
        <div>
            <div>
                <div className="mt-5">
                    <p className="text-deepPurple font-semibold text-lg text-left" id="headline">
                        {t('headline')}
                    </p>
                    <input
                        id="headline-input"
                        className={`coc-underline-input w-full text-deepPurple border py-3 border-x-0 border-t-0 ${titleError ? 'border-b-deleteRed placeholder-deleteRed' : 'border-b-deselectPurple'}`}
                        placeholder={titleError ? `${t('typeHeadlineHere')}*` : t('typeHeadlineHere')}
                        style={{ backgroundColor: 'transparent' }}
                        aria-invalid={titleError}
                        onFocus={() => setTitleError(false)}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>
                <div className="mt-6">
                    <p className="text-deepPurple font-semibold text-lg text-left">{t('supportiveText')} </p>
                    <textarea
                        id="supportive-text-input"
                        className="coc-input w-full text-deepPurple mt-2 border border-deselectPurple p-2 rounded-xl resize-none"
                        placeholder={t('typeAnyAdditionalTextHere')}
                        rows="3"
                        style={{ backgroundColor: 'transparent' }}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </div>

                <div className="mt-6">
                    <p className="text-deepPurple font-semibold text-lg text-left">{t('enterLinkOptional')} </p>
                    <input
                        id="link-input"
                        className="coc-input w-full text-deepPurple mt-2 border border-deselectPurple p-3 rounded-xl resize-none"
                        placeholder={t('typeAnyAdditionalTextHere')}
                        style={{ backgroundColor: 'transparent' }}
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                    />
                </div>
            </div>
            {type === 'poll' && (
                <div>
                    <div className="bg-whiteContrast rounded-xl px-4 mt-5 pb-5">
                        {options.map((option, i) => {
                            const showError = optionsError && option === ''
                            const staggerIndex = i < initialOptionCountRef.current ? Math.min(i, OPTION_STAGGER_CAP) : 0
                            return (
                                <div key={i} className="coc-option-row" style={{ '--i': staggerIndex }}>
                                    <p className="text-mudPurple font-semibold text-sm text-left py-3">
                                        {t('addOption')} {i + 1}
                                    </p>
                                    <div className="coc-input flex flex-row items-center justify-between border border-deselectPurple rounded-xl py-3 px-4" data-error={showError}>
                                        <input
                                            id={`poll-option-input-${i}`}
                                            type="text"
                                            style={{ backgroundColor: 'transparent' }}
                                            className={`w-full text-deepPurple text-base ${showError ? 'placeholder-deleteRed' : ''}`}
                                            value={option}
                                            onChange={(e) => handleOptionInput(e, i)}
                                            placeholder={showError ? `${t('writeOption', { result: i + 1 })}*` : t('writeOption', { result: i + 1 })}
                                            onFocus={() => setOptionsError(false)}
                                        />
                                        {options.length > MINIMAL_NUMBER_OF_POLL_OPTIONS && (
                                            <button
                                                type="button"
                                                onClick={() => handleOptionRemove(i)}
                                                className="coc-icon-button pointer"
                                                aria-label={`${t('deleteOption')} ${i + 1}`}
                                                data-tip={t('deleteOption')}
                                                data-for={POLL_OPTION_TOOLTIP_ID}
                                            >
                                                <OptionRemove fill="#FFA28D" aria-hidden="true" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                        <ReactTooltip id={POLL_OPTION_TOOLTIP_ID} arrowColor="#5E639B" effect="solid" backgroundColor="#5E639B" className="tooltip-poll" />
                        <button
                            type="button"
                            id="add-option-button"
                            className="coc-button coc-button-add mt-5 w-full bg-mudPurple pointer rounded-xl flex justify-center text-white font-semibold text-base items-center py-3 px-4"
                            onClick={handleOptionAdd}
                        >
                            {t('addOption')}
                        </button>
                    </div>
                </div>
            )}
            <div className="mt-6 flex flex-row justify-between gap-[20px]">
                <div className="course-media-container" style={{ alignItems: 'center' }}>
                    <div key={image ? 'preview' : 'empty'} className="coc-fade-in">
                        {!image && (
                            <div className="flex flex-col items-center justify-center bg-containerGray rounded-xl py-8 w-[334px]">
                                <ImageAddIcon style={{ width: 42, height: 42, color: '#8484b1' }} aria-hidden="true" />
                                <p className="text-mudPurple text-base w-[160px] font-[500] text-center">{t('The maximum image size for download is 1400px x 1400px')}</p>
                                <button
                                    type="button"
                                    id="add-photo-video-button"
                                    className="coc-button coc-button-secondary text-deepPurple rounded-full bg-lightBG py-1 font-semibold text-lg px-5 mt-3"
                                    onClick={pressUploadPicture}
                                >
                                    {t('addPhotoVideo')}
                                </button>
                            </div>
                        )}
                        {image && (
                            <div className="flex relative max-h-[244px] flex-col items-center justify-center bg-containerGray rounded-xl py-8 w-[334px] overflow-hidden">
                                <button type="button" onClick={closeImage} id="close-image-button" className="coc-media-close absolute top-0 right-0" aria-label={t('remove')}>
                                    <FaTimes style={{ width: 20, height: 20, stroke: 'white', display: 'block' }} aria-hidden="true" />
                                </button>
                                {isVideo ? (
                                    <div className="simage-container-new coc-media" data-loaded={mediaLoaded}>
                                        <video key={image} controls controlsList="nodownload" onLoadedData={() => setMediaLoaded(true)} onError={() => setMediaLoaded(true)}>
                                            <source src={image} />
                                            {t('yourBrowserDoesNotSupportHTMLVideo')}
                                        </video>
                                    </div>
                                ) : (
                                    <img
                                        key={image}
                                        className="coc-media"
                                        data-loaded={mediaLoaded}
                                        style={{ objectFit: 'contain', width: '100%', minHeight: 170, marginTop: 10 }}
                                        src={image}
                                        alt=""
                                        onLoad={() => setMediaLoaded(true)}
                                        onError={() => setMediaLoaded(true)}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        title="Add Video/Photo"
                        style={{ display: 'none' }}
                        accept="video/*, image/*"
                        type="file"
                        id="photo_upload_modal"
                        onChange={onImageChange}
                        onClick={resetInputValue}
                    />
                </div>
                <div className="flex flex-col min-w-[334px]">
                    <div className="flex flex-row justify-between items-center mb-3 h-[40px]">
                        <p className={`coc-label text-lg font-medium ${targetError ? 'text-[#FF747F]' : 'text-mudPurple'}`}>
                            {t('targetGroup')}
                            {targetError ? '*' : ''}
                        </p>
                        <button
                            type="button"
                            id={`target-group-button-${returnTarget?.name}`}
                            className={`coc-pill text-lg font-semibold rounded-full py-1 px-4 bg-containerGray pointer text-center ${targetError ? 'text-[#FF747F]' : hasTarget ? 'text-deepPurple' : 'text-mudPurple'}`}
                            data-error={targetError}
                            onClick={() => setOpenTarget(true)}
                        >
                            {translateTargetText(returnTarget?.name)}
                        </button>
                    </div>
                    <div className="flex flex-row justify-between items-center my-[7px] h-[40px]">
                        <p className="text-mudPurple text-lg font-medium">{t('schedule')}</p>
                        <div className="flex flex-row justify-between items-center my-3 h-[40px]">
                            <ScheduleSwitcher
                                id="schedule-switch"
                                customOnTextEnabled={returnSchedule?.enabled}
                                customOnText={format_day(returnSchedule?.schedule_date, true, i18n.language)}
                                setSwitcher={setSchedule}
                                switcher={returnSchedule?.enabled}
                            />
                        </div>
                    </div>
                    {returnRepeat?.repeat && (
                        <div className="coc-fade-in flex flex-row justify-between items-center my-3 h-[40px]">
                            <p className="text-mudPurple text-lg font-medium">{t('repeat')}</p>
                            <button
                                type="button"
                                className="coc-pill pointer flex flex-row justify-between items-center my-[7px] min-h-[40px] max-w-[210px] px-[15px] bg-containerGray rounded-full"
                                onClick={setSchedule}
                            >
                                <span className="font-semibold text-lg text-[#5E639B] leading-[20px] text-center">{returnRepeat?.repeatText}</span>
                            </button>
                        </div>
                    )}
                    {type !== 'feed' && (
                        <div className="flex flex-row justify-between items-center my-[7px] h-[40px] gap-[10px]">
                            <p className="text-mudPurple text-lg font-medium flex-1">{t('addToWellbeingTracker')} </p>
                            <Switcher setSwitcher={switchAddWellbeingTracker} switcher={trackWellbeingEnabled} id="add-wellbeing-switcher" />
                        </div>
                    )}
                    {type !== 'feed' && (
                        <div className="flex flex-row justify-between items-center my-[7px] h-[40px] gap-[10px]">
                            <p className="text-mudPurple text-lg font-medium flex-1">{t('anonymous')} </p>
                            <Switcher setSwitcher={switchAnonymous} switcher={anonymousEnabled} id="add-anonymous-switcher" />
                        </div>
                    )}
                    {type === 'poll' && !!returnSchedule?.repeatEnabled && (
                        <div className="coc-fade-in flex flex-row justify-between items-center my-[7px] h-[40px]">
                            <p className="text-mudPurple text-lg font-medium">{t('addToAnalytics')}</p>
                            <Switcher setSwitcher={setAddedToAnalytics} switcher={addedToAnalytics} id="add-analytics-switcher" />
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-2">
                <p className="coc-error-line text-[#FF747F] font-medium text-[14px] text-center" data-visible={showErrorMessage} aria-live="polite">
                    *{t('pleaseFillRequiredFields')}
                </p>
            </div>
            <div className={`flex flex-row ${editTemplateMode ? 'justify-center' : 'justify-between'} mt-2`}>
                <button
                    type="button"
                    id="save-template-button"
                    className="coc-button coc-button-outline !border !border-solid rounded-full py-3 flex min-w-[334px] justify-center items-center font-semibold text-lg"
                    disabled={templateSaved || savingTemplate}
                    data-saved={templateSaved}
                    aria-haspopup="dialog"
                    onClick={openSaveTemplate}
                >
                    {savingTemplate ? <Loading primaryColor="#5E639B" /> : editTemplateMode ? t('updateTemplate') : templateSaved ? t('templateIsSaved') : t('saveAsTemplate')}
                </button>
                {!editTemplateMode && (
                    <button
                        type="button"
                        id="publish-button"
                        className="coc-button coc-button-primary rounded-full py-3 flex justify-center min-w-[334px] items-center font-semibold text-lg"
                        onClick={handlePublish}
                        disabled={progress}
                    >
                        {progress ? <Loading /> : isUpdate ? t('update') : t('publish')}
                    </button>
                )}
            </div>
            <SaveTemplateModal
                isOpen={saveModalOpen}
                onClose={() => setSaveModalOpen(false)}
                onConfirm={handleSaveTemplate}
                mode={editTemplateMode ? 'update' : 'create'}
                initialVisibility={editTemplateMode ? selectedContent?.visibility ?? VISIBILITY_PRIVATE : VISIBILITY_PRIVATE}
                editingAuthorName={editTemplateMode && selectedContent?.author && String(selectedContent.author.id) !== String(user.id) ? authorLabel(selectedContent.author, user, t) : ''}
            />
            <ScheduleExpandedModal showSchedule={openSchedule} closeSchedule={closeScheduleModal} data={returnSchedule} setReturnSchedule={setReturnDataPreStep} />
            <TargetGroupModal showTarget={openTarget} closeTarget={closeTargetModal} setReturnTarget={setReturnTarget} />
        </div>
    )
}

export default CreateOwnContent
